import type { ComponentProps } from "react"
import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import UptimeMuteDialog from "@/v2-backoffice-client/uptime/mute/UptimeMuteDialog"
import { useUptimeClearMuteMutation } from "@/v2-backoffice-client/uptime/mute/clearMute/uptime.clearMute.mutation"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type UptimeMuteButtonProps = Omit<
  ComponentProps<typeof TooltipButton>,
  "content" | "children" | "iconSrc"
> & {
  target: UptimeTargetFragment
}

// UptimeMuteButton renders the mute / unmute action that lives in the
// view sheet toolbar. When the target is currently muted it un-mutes
// inline (no dialog -- the click itself is the confirmation, and
// re-muting is one more click); otherwise it opens the reason dialog.
function UptimeMuteButton(props: UptimeMuteButtonProps) {
  const { target, ...rest } = props
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const [clearMute, { loading: clearing, error: clearError }] =
    useUptimeClearMuteMutation()
  errorToast(clearError ?? null, { title: "Failed to unmute target" })

  if (target.IsMuted) {
    return (
      <TooltipButton
        variant="ghost"
        isStacked
        iconSrc="bell"
        content="Resume alerts for this target"
        isLoading={clearing}
        onClick={() => {
          clearMute({
            variables: {
              input: {
                source: target.Source,
                identifier: target.Identifier,
              },
            },
          })
        }}
        {...rest}
      >
        Unmute
      </TooltipButton>
    )
  }

  return (
    <>
      <TooltipButton
        variant="ghost"
        isStacked
        iconSrc="bell-slash"
        content="Silence this target so it stops appearing in KPIs and Active incidents"
        onClick={() => {
          setIsDialogOpen(true)
        }}
        {...rest}
      >
        Mute
      </TooltipButton>
      <UptimeMuteDialog
        target={target}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </>
  )
}

export default UptimeMuteButton
